<!-- Enhanced AssumptionsCard.vue with Reference Data -->
<template>
  <div class="assumptions-card">
    <div class="card-header">
      <h5>Planning Assumptions & Tax Reference</h5>
      <div class="header-controls">
        <button class="edit-toggle" @click="toggleEditMode" :class="{ editing: isEditing }">
          <svg v-if="!isEditing" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 6 9 17l-5-5"/>
          </svg>
          {{ isEditing ? 'Done' : 'Edit' }}
        </button>
        <button class="reference-toggle" @click="toggleReferenceData" :class="{ active: showReferenceData }">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="m9 12 2 2 4-4"/>
          </svg>
          Tax Reference
        </button>
      </div>
    </div>

    <!-- EDITABLE ASSUMPTIONS SECTION -->
    <div class="editable-section">
      <h6 class="section-title">Your Planning Assumptions (Editable)</h6>
      
      <!-- Read Mode -->
      <div v-if="!isEditing" class="read-mode">
        <div class="assumptions-grid">
          <!-- Economic Assumptions -->
          <div class="assumption-category">
            <span class="category-label">Economic Rates:</span>
          </div>
          <div class="assumption-item">
            <span class="assumption-label">Inflation Rate:</span>
            <span class="assumption-value">{{ formatPercentage(assumptions.inflationRate) }}</span>
          </div>
          <div class="assumption-item">
            <span class="assumption-label">Income Growth:</span>
            <span class="assumption-value">{{ formatPercentage(assumptions.defaultIncomeGrowth) }}</span>
          </div>
          <div class="assumption-item">
            <span class="assumption-label">Investment Growth:</span>
            <span class="assumption-value">{{ formatPercentage(assumptions.defaultAssetGrowth) }}</span>
          </div>

          <!-- Tax Situation -->
          <div class="assumption-category">
            <span class="category-label">Your Tax Situation:</span>
          </div>
          <div class="assumption-item">
            <span class="assumption-label">Filing Status:</span>
            <span class="assumption-value">{{ getFilingStatusDisplay(assumptions.currentFilingStatus) }}</span>
          </div>
          <div class="assumption-item">
            <span class="assumption-label">Current State:</span>
            <span class="assumption-value">{{ getStateDisplay(assumptions.currentState) }}</span>
          </div>
          <div class="assumption-item">
            <span class="assumption-label">Retirement State:</span>
            <span class="assumption-value">{{ getStateDisplay(assumptions.retirementState) }}</span>
          </div>

          <!-- Benefits -->
          <div class="assumption-category">
            <span class="category-label">Government Benefits:</span>
          </div>
          <div class="assumption-item">
            <span class="assumption-label">Social Security Age:</span>
            <span class="assumption-value">{{ assumptions.socialSecurityStartAge }}</span>
          </div>
          <div class="assumption-item">
            <span class="assumption-label">Medicare Age:</span>
            <span class="assumption-value">{{ assumptions.medicareStartAge }}</span>
          </div>
        </div>
      </div>

      <!-- Edit Mode -->
      <div v-else class="edit-mode">
        <div class="edit-sections">
          <div class="edit-section">
            <h6>Economic Rates</h6>
            <div class="edit-grid">
              <div class="edit-field">
                <label>Inflation Rate (%)</label>
                <input
                  :value="(assumptions.inflationRate * 100).toFixed(2)"
                  @input="updateField('inflationRate', Number($event.target.value) / 100)"
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  class="field-input"
                />
              </div>
              <div class="edit-field">
                <label>Income Growth (%)</label>
                <input
                  :value="(assumptions.defaultIncomeGrowth * 100).toFixed(2)"
                  @input="updateField('defaultIncomeGrowth', Number($event.target.value) / 100)"
                  type="number"
                  step="0.1"
                  min="0"
                  max="15"
                  class="field-input"
                />
              </div>
              <div class="edit-field">
                <label>Investment Growth (%)</label>
                <input
                  :value="(assumptions.defaultAssetGrowth * 100).toFixed(2)"
                  @input="updateField('defaultAssetGrowth', Number($event.target.value) / 100)"
                  type="number"
                  step="0.1"
                  min="0"
                  max="20"
                  class="field-input"
                />
              </div>
            </div>
          </div>

          <div class="edit-section">
            <h6>Tax Situation</h6>
            <div class="edit-grid">
              <div class="edit-field">
                <label>Filing Status</label>
                <select
                  :value="assumptions.currentFilingStatus"
                  @change="updateField('currentFilingStatus', $event.target.value)"
                  class="field-select"
                >
                  <option value="single">Single</option>
                  <option value="marriedJoint">Married Filing Jointly</option>
                  <option value="marriedSeparate">Married Filing Separately</option>
                  <option value="headOfHousehold">Head of Household</option>
                </select>
              </div>
              <div class="edit-field">
                <label>Current State</label>
                <select
                  :value="assumptions.currentState"
                  @change="updateField('currentState', $event.target.value)"
                  class="field-select state-select"
                >
                  <option v-for="state in stateOptions" :key="state.value" :value="state.value">
                    {{ state.label }}
                  </option>
                </select>
              </div>
              <div class="edit-field">
                <label>Retirement State</label>
                <select
                  :value="assumptions.retirementState"
                  @change="updateField('retirementState', $event.target.value)"
                  class="field-select state-select"
                >
                  <option v-for="state in stateOptions" :key="state.value" :value="state.value">
                    {{ state.label }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="edit-section">
            <h6>Government Benefits</h6>
            <div class="edit-grid">
              <div class="edit-field">
                <label>Social Security Age</label>
                <input
                  :value="assumptions.socialSecurityStartAge"
                  @input="updateField('socialSecurityStartAge', Number($event.target.value))"
                  type="number"
                  min="62"
                  max="70"
                  class="field-input"
                />
              </div>
              <div class="edit-field">
                <label>Medicare Age</label>
                <input
                  :value="assumptions.medicareStartAge"
                  @input="updateField('medicareStartAge', Number($event.target.value))"
                  type="number"
                  min="65"
                  max="67"
                  class="field-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="edit-actions">
          <button @click="toggleEditMode" class="btn-done">✓ Done</button>
          <button @click="resetToDefaults" class="btn-reset">Reset Defaults</button>
        </div>
      </div>
    </div>

    <!-- TAX REFERENCE DATA SECTION (Non-editable) -->
    <div v-if="showReferenceData" class="reference-section">
      <h6 class="section-title">Tax Reference Data (2025) - Non-editable</h6>
      
      <div class="reference-tabs">
        <button 
          @click="activeReferenceTab = 'federal'"
          class="reference-tab"
          :class="{ active: activeReferenceTab === 'federal' }"
        >
          Federal Brackets
        </button>
        <button 
          @click="activeReferenceTab = 'state'"
          class="reference-tab"
          :class="{ active: activeReferenceTab === 'state' }"
        >
          State Rates
        </button>
        <button 
          @click="activeReferenceTab = 'fica'"
          class="reference-tab"
          :class="{ active: activeReferenceTab === 'fica' }"
        >
          FICA Rates
        </button>
      </div>

      <div class="reference-content">
        <!-- Federal Tax Brackets -->
        <div
          v-if="activeReferenceTab === 'federal'"
          class="federal-brackets"
        >
          <div class="bracket-filing-status">
            <label>Filing Status:</label>
            <select
              v-model="selectedFilingStatus"
              class="field-select"
            >
              <option value="single">
                Single
              </option>
              <option value="marriedJoint">
                Married Filing Jointly
              </option>
              <option value="marriedSeparate">
                Married Filing Separately
              </option>
              <option value="headOfHousehold">
                Head of Household
              </option>
            </select>
          </div>

          <div class="brackets-table">
            <div class="table-header">
              <div class="col-rate">
                Tax Rate
              </div>
              <div class="col-range">
                Income Range
              </div>
            </div>
            <div
              v-for="(bracket, index) in federalBrackets"
              :key="index"
              class="bracket-row"
            >
              <div class="col-rate">
                {{ formatPercentage(bracket.rate) }}
              </div>
              <div class="col-range">
                ${{ formatNumber(bracket.min) }} -
                {{ bracket.max === Infinity ? 'No limit' : formatNumber(bracket.max) }}
              </div>
            </div>
          </div>
        </div>

        <!-- State Tax Rates -->
        <div v-if="activeReferenceTab === 'state'" class="state-rates">
          <div class="state-filter">
            <input 
              v-model="stateFilter" 
              type="text" 
              placeholder="Search states..." 
              class="field-input"
            />
          </div>
          
          <div class="states-grid">
            <div 
              v-for="state in filteredStates" 
              :key="state.code"
              class="state-card"
              :class="{ 
                'no-tax': !state.hasIncomeTax,
                'capital-gains-only': !state.hasGeneralIncomeTax && state.hasCapitalGainsTax,
                'dividend-only': state.hasDividendInterestTax && !state.hasGeneralIncomeTax && !state.hasCapitalGainsTax
              }"
            >
              <div class="state-name">{{ state.name }} ({{ state.code }})</div>
              <div class="state-rate">
                <span v-if="!state.hasIncomeTax">No income tax</span>
                <span v-else-if="!state.hasGeneralIncomeTax && state.hasCapitalGainsTax">
                  {{ formatPercentage(state.topCapitalGainsRate) }} capital gains tax
                  <span v-if="state.capitalGainsExemption" class="exemption-note">
                    (${{ formatNumber(state.capitalGainsExemption) }} exemption)
                  </span>
                </span>
                <span v-else-if="state.hasDividendInterestTax && !state.hasGeneralIncomeTax">
                  Dividends/Interest only
                </span>
                <span v-else>
                  {{ formatPercentage(state.topRate) }} top rate
                </span>
              </div>
              <div class="state-brackets" v-if="state.hasGeneralIncomeTax">
                {{ state.brackets }} bracket{{ state.brackets === 1 ? '' : 's' }}
                <span v-if="state.hasCapitalGainsTax && state.topCapitalGainsRate !== state.topOrdinaryRate">
                  + capital gains
                </span>
              </div>
              <div class="state-special" v-if="state.hasCapitalGainsTax && !state.hasGeneralIncomeTax">
                Capital gains only
              </div>
            </div>
          </div>
        </div>

        <!-- FICA Rates -->
        <div v-if="activeReferenceTab === 'fica'" class="fica-rates">
          <div class="fica-section">
            <h6>Social Security Tax</h6>
            <div class="fica-details">
              <div class="fica-row">
                <span>Employee Rate:</span>
                <span>{{ formatPercentage(ficaRates.socialSecurity.employeeRate) }}</span>
              </div>
              <div class="fica-row">
                <span>Employer Rate:</span>
                <span>{{ formatPercentage(ficaRates.socialSecurity.employerRate) }}</span>
              </div>
              <div class="fica-row">
                <span>Wage Base Limit:</span>
                <span>${{ formatNumber(ficaRates.socialSecurity.wageBase) }}</span>
              </div>
              <div class="fica-row">
                <span>Self-Employed Rate:</span>
                <span>{{ formatPercentage(ficaRates.socialSecurity.totalRate) }}</span>
              </div>
            </div>
          </div>

          <div class="fica-section">
            <h6>Medicare Tax</h6>
            <div class="fica-details">
              <div class="fica-row">
                <span>Employee Rate:</span>
                <span>{{ formatPercentage(ficaRates.medicare.employeeRate) }}</span>
              </div>
              <div class="fica-row">
                <span>Employer Rate:</span>
                <span>{{ formatPercentage(ficaRates.medicare.employerRate) }}</span>
              </div>
              <div class="fica-row">
                <span>Wage Base Limit:</span>
                <span>No limit</span>
              </div>
              <div class="fica-row">
                <span>Additional Medicare Tax:</span>
                <span>{{ formatPercentage(ficaRates.medicare.additionalRate) }} (on income over ${{ formatNumber(ficaRates.medicare.additionalThreshold.single) }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { 
  FEDERAL_TAX_BRACKETS_2025, 
  FICA_RATES_2025, 
  STATE_TAX_DATA_2025,
  getStateOptions,
  formatTaxRate,
  formatCurrency,
  TAX_CALCULATORS
} from './planning_assumptions/StateTaxReference2025.js';

export default {
  name: 'AssumptionsCard',
  props: {
    assumptions: {
      type: Object,
      required: true
    }
  },
  emits: ['update-assumptions'],
  setup(props, { emit }) {
    const isEditing = ref(false);
    const showReferenceData = ref(false);
    const activeReferenceTab = ref('federal');
    const selectedFilingStatus = ref('single');
    const stateFilter = ref('');

    // Real tax data from reference system
    const federalBracketsData = FEDERAL_TAX_BRACKETS_2025;
    const ficaRates = FICA_RATES_2025;

    // Process state data for display
    const stateData = computed(() => {
      return Object.entries(STATE_TAX_DATA_2025).map(([code, data]) => {
        const topOrdinaryRate = data.hasGeneralIncomeTax && data.singleFiler.brackets.length > 0 ? 
          Math.max(...data.singleFiler.brackets.map(b => b.rate)) : 0;
        
        const topCapitalGainsRate = data.hasCapitalGainsTax && data.singleFiler.capitalGainsBrackets ? 
          Math.max(...data.singleFiler.capitalGainsBrackets.map(b => b.rate)) : 0;
        
        return {
          code,
          name: data.name,
          hasIncomeTax: data.hasIncomeTax,
          hasGeneralIncomeTax: data.hasGeneralIncomeTax,
          hasCapitalGainsTax: data.hasCapitalGainsTax,
          hasDividendInterestTax: data.hasDividendInterestTax,
          topRate: Math.max(topOrdinaryRate, topCapitalGainsRate),
          topOrdinaryRate,
          topCapitalGainsRate,
          brackets: data.singleFiler.brackets.length,
          capitalGainsExemption: data.singleFiler?.capitalGainsExemption,
          description: data.description
        };
      }).sort((a, b) => a.name.localeCompare(b.name));
    });

    // Get state options from reference system
    const stateOptions = computed(() => getStateOptions());

    const federalBrackets = computed(() => 
      federalBracketsData[selectedFilingStatus.value] || federalBracketsData.single
    );

    const filteredStates = computed(() => 
      stateData.value.filter(state => 
        state.name.toLowerCase().includes(stateFilter.value.toLowerCase()) ||
        state.code.toLowerCase().includes(stateFilter.value.toLowerCase())
      )
    );

    const formatPercentage = (value) => {
      return formatTaxRate(value);
    };

    const formatNumber = (value) => {
      return new Intl.NumberFormat('en-US').format(value);
    };

    const toggleEditMode = () => {
      isEditing.value = !isEditing.value;
    };

    const toggleReferenceData = () => {
      showReferenceData.value = !showReferenceData.value;
    };

    const updateField = (field, value) => {
      emit('update-assumptions', field, value);
    };

    const resetToDefaults = () => {
      const defaults = {
        inflationRate: 0.025,
        defaultIncomeGrowth: 0.025,
        defaultAssetGrowth: 0.07,
        socialSecurityStartAge: 67,
        medicareStartAge: 65,
        currentFilingStatus: 'single',
        currentState: 'NY',
        retirementState: 'FL'
      };

      Object.keys(defaults).forEach(key => {
        emit('update-assumptions', key, defaults[key]);
      });
    };

    const getFilingStatusDisplay = (status) => {
      const statuses = {
        single: 'Single',
        marriedJoint: 'Married Filing Jointly',
        marriedSeparate: 'Married Filing Separately',
        headOfHousehold: 'Head of Household'
      };
      return statuses[status] || status;
    };

    const getStateDisplay = (stateCode) => {
      const state = stateData.value.find(s => s.code === stateCode);
      return state ? state.name : stateCode;
    };

    return {
      isEditing,
      showReferenceData,
      activeReferenceTab,
      selectedFilingStatus,
      stateFilter,
      stateOptions,
      federalBrackets,
      filteredStates,
      ficaRates,
      formatPercentage,
      formatNumber,
      toggleEditMode,
      toggleReferenceData,
      updateField,
      resetToDefaults,
      getFilingStatusDisplay,
      getStateDisplay
    };
  }
};
</script>

<style scoped>
.assumptions-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.card-header {
  background: white;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #212529;
}

.header-controls {
  display: flex;
  gap: 10px;
}

.edit-toggle, .reference-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
}

.edit-toggle:hover, .reference-toggle:hover {
  background: #f8f9fa;
}

.edit-toggle.editing, .reference-toggle.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.section-title {
  margin: 0 0 15px 0;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.editable-section {
  padding: 20px;
  background: white;
}

.assumptions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;
}

.assumption-category {
  grid-column: 1 / -1;
  margin-top: 20px;
  margin-bottom: 8px;
}

.assumption-category:first-child {
  margin-top: 0;
}

.category-label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.assumption-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f8f9fa;
}

.assumption-label {
  font-size: 13px;
  color: #495057;
}

.assumption-value {
  font-size: 13px;
  font-weight: 600;
  color: #212529;
}

.edit-sections {
  margin-bottom: 20px;
}

.edit-section {
  margin-bottom: 25px;
}

.edit-section h6 {
  margin: 0 0 12px 0;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 8px;
}

.edit-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.edit-field {
  display: flex;
  flex-direction: column;
}

.edit-field label {
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
}

.field-input, .field-select {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  background: white;
}

.field-input:focus, .field-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0,123,255,.25);
}

.state-select {
  font-size: 11px;
}

.edit-actions {
  display: flex;
  gap: 10px;
  padding-top: 15px;
  border-top: 1px solid #e9ecef;
}

.btn-done, .btn-reset {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-done {
  background: #28a745;
  color: white;
  border: 1px solid #28a745;
}

.btn-done:hover {
  background: #218838;
}

.btn-reset {
  background: white;
  color: #6c757d;
  border: 1px solid #ced4da;
}

.btn-reset:hover {
  background: #f8f9fa;
}

.reference-section {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  padding: 20px;
}

.reference-tabs {
  display: flex;
  gap: 5px;
  margin-bottom: 20px;
}

.reference-tab {
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
}

.reference-tab:hover {
  background: #f8f9fa;
}

.reference-tab.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.reference-content {
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.bracket-filing-status {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.bracket-filing-status label {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.brackets-table {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 100px 1fr;
  background: #f8f9fa;
  font-weight: 600;
  font-size: 13px;
  color: #495057;
}

.table-header > div, .bracket-row > div {
  padding: 12px 15px;
  border-right: 1px solid #e9ecef;
}

.table-header > div:last-child, .bracket-row > div:last-child {
  border-right: none;
}

.bracket-row {
  display: grid;
  grid-template-columns: 100px 1fr;
  border-bottom: 1px solid #e9ecef;
  font-size: 13px;
}

.bracket-row:last-child {
  border-bottom: none;
}

.bracket-row:nth-child(even) {
  background: #f8f9fa;
}

.state-filter {
  margin-bottom: 20px;
}

.states-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.state-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 15px;
  transition: all 0.2s;
}

.state-card:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0,123,255,.15);
}

.state-card.capital-gains-only {
  background: #fff3cd;
  border-color: #ffeaa7;
}

.state-card.dividend-only {
  background: #e2e3e5;
  border-color: #c6c8ca;
}

.exemption-note {
  font-size: 11px;
  color: #6c757d;
  display: block;
  margin-top: 2px;
}

.state-special {
  font-size: 11px;
  color: #856404;
  font-style: italic;
}

.state-card.no-tax {
  background: #d4edda;
  border-color: #c3e6cb;
}

.state-name {
  font-weight: 600;
  font-size: 14px;
  color: #212529;
  margin-bottom: 5px;
}

.state-rate {
  font-size: 13px;
  color: #495057;
  margin-bottom: 3px;
}

.state-brackets {
  font-size: 11px;
  color: #6c757d;
}

.fica-section {
  margin-bottom: 25px;
}

.fica-section h6 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

.fica-details {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 15px;
}

.fica-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
  font-size: 13px;
}

.fica-row:last-child {
  border-bottom: none;
}

.fica-row span:first-child {
  color: #495057;
}

.fica-row span:last-child {
  font-weight: 600;
  color: #212529;
}

@media (max-width: 768px) {
  .assumptions-grid {
    grid-template-columns: 1fr;
  }
  
  .edit-grid {
    grid-template-columns: 1fr;
  }
  
  .states-grid {
    grid-template-columns: 1fr;
  }
  
  .header-controls {
    flex-direction: column;
    gap: 8px;
  }
}
</style>